import { Eye, EyeSlash, Plus, Trash, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { SearchSelect } from "@/components/SearchSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColorPicker, ShapeIcon } from "./shared";

export const EdgesTab = ({ d, set, setEdge, opts, serviceOptions }) => (
  <div className="mt-6 space-y-3" data-testid="drawing-edges">
    <div className="grid gap-3 xl:grid-cols-2">
      {d.edges.map((e, i) => (
        <div
          key={e.id || i}
          data-testid={`edge-style-${i}`}
          className={`rounded-lg border bg-zinc-900 p-3 transition-colors duration-150 ${
            e.visible === false
              ? "border-zinc-800 opacity-60"
              : "border-zinc-800 hover:border-indigo-400/40"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <ShapeIcon shape={e.shape} color={e.color} />
            <Select value={e.shape} onValueChange={(v) => setEdge(i, { shape: v })}>
              <SelectTrigger
                data-testid={`edge-shape-${i}`}
                className="h-10 w-28 border-zinc-800 bg-zinc-950 text-[11px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {opts.shapes.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ColorPicker
              testId={`edge-color-${i}`}
              value={e.color}
              options={opts.colors}
              onChange={(v) => setEdge(i, { color: v })}
            />
            <Input
              data-testid={`edge-name-${i}`}
              value={e.name}
              onChange={(ev) => setEdge(i, { name: ev.target.value })}
              placeholder="Profile name"
              className="h-10 min-w-[120px] flex-1 border-zinc-800 bg-zinc-950 text-sm font-semibold"
            />
            <Input
              data-testid={`edge-abbr-${i}`}
              value={e.abbr}
              onChange={(ev) => setEdge(i, { abbr: ev.target.value.toUpperCase() })}
              placeholder="ABBR"
              className="font-mono-data h-10 w-20 border-zinc-800 bg-zinc-950 text-center text-xs"
            />
            <Input
              data-testid={`edge-alias-${i}`}
              value={e.alias || ""}
              onChange={(ev) => setEdge(i, { alias: ev.target.value })}
              placeholder="Shop code"
              title="Shop shorthand shown on the drawing and the cut sheet (overrides ABBR)"
              className="font-mono-data h-10 w-24 border-zinc-800 bg-zinc-950 text-center text-xs"
            />
            <button
              type="button"
              data-testid={`edge-visible-${i}`}
              title={e.visible === false ? "Hidden from estimators" : "Shown to estimators"}
              onClick={() => setEdge(i, { visible: e.visible === false })}
              className={`rounded-lg border p-2 transition-colors duration-150 ${
                e.visible === false
                  ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
                  : "border-zinc-700 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {e.visible === false ? (
                <EyeSlash size={14} weight="bold" />
              ) : (
                <Eye size={14} weight="bold" />
              )}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid={`edge-default-${i}`}
              onClick={() =>
                set({
                  edges: d.edges.map((x, idx) => ({ ...x, default: idx === i })),
                })
              }
              className={`rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                e.default
                  ? "border-indigo-400/50 bg-indigo-500/15 text-indigo-200"
                  : "border-zinc-700 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {e.default ? "Default" : "Set default"}
            </button>
            <div className="min-w-[180px] flex-1">
              <SearchSelect
                testId={`edge-service-${i}`}
                value={e.service_id || ""}
                options={serviceOptions}
                placeholder="Link a service (sets the price)"
                searchPlaceholder="Search services…"
                emptyText="Add services on the Services page"
                onChange={(v) => setEdge(i, { service_id: v })}
                className="h-10 px-3 text-xs"
              />
            </div>
            {e.service_id && (
              <button
                type="button"
                data-testid={`edge-service-clear-${i}`}
                onClick={() => setEdge(i, { service_id: "" })}
                className="rounded-lg border border-zinc-700 p-2 text-zinc-500 transition-colors duration-150 hover:text-zinc-200"
              >
                <X size={14} weight="bold" />
              </button>
            )}
            <button
              type="button"
              data-testid={`edge-delete-${i}`}
              onClick={() =>
                set({ edges: d.edges.filter((_, idx) => idx !== i) })
              }
              className="rounded-lg border border-zinc-700 p-2 text-zinc-500 transition-colors duration-150 hover:border-red-500/40 hover:text-red-400"
            >
              <Trash size={14} weight="bold" />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="add-edge-style"
        onClick={() =>
          set({
            edges: [
              ...d.edges,
              {
                id: `e${Date.now()}`,
                name: "New profile",
                abbr: "",
                alias: "",
                shape: "eased",
                color: opts.colors[0] || "#34d399",
                service_id: "",
                visible: true,
                default: false,
              },
            ],
          })
        }
        className="flex min-h-[104px] items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-700 text-[11px] font-bold uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:border-indigo-400/60 hover:bg-zinc-900 hover:text-indigo-200"
      >
        <Plus size={16} weight="bold" /> Add edge profile
      </button>
    </div>
  </div>
);
